/**
 * Widget Modular Service
 * Gerencia requisições para widget-modulars no Strapi
 */

import envConfig from '@config/env.js';
import StrapiService from './StrapiService.js';
import { transformStrapiV4 } from './transformers.js';

function toSlug(value) {
    if (!value) return '';
    return String(value)
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
}

/**
 * Serviço para gerenciar Widget Modulars
 */
class WidgetModularService {
    constructor(config = {}) {
        // Carrega configuração do ambiente
        const apiUrl = config.apiUrl || envConfig.get('STRAPI_API_URL');
        const apiToken = config.apiToken || envConfig.get('STRAPI_API_TOKEN');
        const timeout = config.timeout || envConfig.get('API_TIMEOUT');
        const cacheEnabled = config.cacheEnabled !== undefined
            ? config.cacheEnabled
            : envConfig.get('CACHE_ENABLED');
        const cacheExpiry = config.cacheExpiry || envConfig.get('CACHE_TTL');

        // Inicializa StrapiService
        this.strapi = new StrapiService({
            baseUrl: apiUrl,
            apiToken: apiToken,
            timeout: timeout,
            enableCache: cacheEnabled,
            cacheExpiry: cacheExpiry
        });

        this.endpoint = 'widget-modulars';
    }

    /**
     * Busca widget por documentId
     * @param {string} documentId - Document ID do widget (ex: "zdcpwcgpjhzyputt946rnlbp")
     * @param {Object} options - Opções adicionais
     * @returns {Promise<Object>}
     */
    async getByDocumentId(documentId, options = {}) {
        try {
            // Populate específico para cada campo
            const params = {
                'populate[bloco_imagem_texto][populate]': '*',
                'populate[Cards][populate]': '*',
                'populate[Editor_HTML][populate]': '*',
                // populate=* só expande 1 nível: sem o populate explícito abaixo,
                // oferta_formulario.oferta.generic_models (2 níveis) nunca vem
                // populado, e a pergunta "Qual modelo você tem interesse?" some.
                'populate[oferta_formulario][populate][cliente]': '*',
                'populate[oferta_formulario][populate][oferta][populate][generic_models][populate]': 'montadora',
                'populate[formulario_simples][populate]': '*',
                'populate[minimal_data][populate]': '*',
                'populate[Marcas]': '*',
                ...options.params
            };

            const data = await this.strapi.get(`${this.endpoint}/${documentId}`, params);
            return transformStrapiV4(data);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Extrai componentes ativos do widget baseado na configuração
     * @param {Object} widgetData - Dados transformados do widget
     * @param {Object} config - Configuração de componentes ativos (ex: {cards: true, bloco_imagem_texto: true})
     * @param {string|null} codigoAfiliado - Código do afiliado cadastrado no widget
     * @param {string|null} utmTerm - Termo UTM dinâmico por widget-modular
     * @returns {Object} Componentes formatados
     */
    extractActiveComponents(widgetData, config = {}, codigoAfiliado = null, utmTerm = null) {
        const components = {};

        // Bloco Imagem/Texto
        if (config.bloco_imagem_texto && widgetData.bloco_imagem_texto) {
            // Extrai URL da imagem
            let imagemUrl = null;
            if (widgetData.bloco_imagem_texto.imagem_principal) {
                const img = widgetData.bloco_imagem_texto.imagem_principal;
                // Usa formato thumbnail ou URL original
                imagemUrl = img.formats?.thumbnail?.url || img.url || null;

                // Adiciona base URL do Strapi se necessário
                if (imagemUrl && !imagemUrl.startsWith('http')) {
                    imagemUrl = `${envConfig.get('STRAPI_API_URL')}${imagemUrl}`;
                }
            }

            components.bloco_imagem_texto = {
                id: widgetData.bloco_imagem_texto.id,
                chamada_principal: widgetData.bloco_imagem_texto.Chamada_principal || '',
                imagem: imagemUrl
            };
        }

        // Cards (CarouselModule)
        if (config.cards && widgetData.Cards && Array.isArray(widgetData.Cards) && widgetData.Cards.length > 0) {
            components.cards = widgetData.Cards.map(card => {
                // Processa imagem do card (se existir)
                // Campo Imagem é um array no Strapi, pega o primeiro elemento
                let cardImage = null;
                if (card.Imagem && Array.isArray(card.Imagem) && card.Imagem.length > 0) {
                    const img = card.Imagem[0];
                    cardImage = img.formats?.large?.url || img.url || null;

                    // Adiciona base URL do Strapi se necessário
                    if (cardImage && !cardImage.startsWith('http')) {
                        cardImage = `${envConfig.get('STRAPI_API_URL')}${cardImage}`;
                    }
                }

                // Processa o campo Link
                let cardLink = '#';
                let linkInfo = null;

                if (card.Link) {
                    if (typeof card.Link === 'object' && card.Link !== null) {
                        // Novo formato: Link é um objeto com informações da oferta
                        linkInfo = {
                            id: card.Link.id,
                            documentId: card.Link.documentId,
                            title: card.Link.title || card.Link.offer_title,
                            slug: card.Link.slug,
                            canonical_url: card.Link.canonical_url,
                            isDraft: card.Link.isDraft,
                            isPublished: card.Link.isPublished
                        };

                        // Usa canonical_url se disponível, senão constrói a partir do slug
                        let baseUrl = '';
                        if (card.Link.canonical_url) {
                            baseUrl = card.Link.canonical_url;
                        } else if (card.Link.slug) {
                            // Constrói URL básica com o slug
                            baseUrl = `https://mundozerokm.com.br/ofertas/${card.Link.slug}`;
                        }

                        // Adiciona parâmetros de tracking
                        if (baseUrl && baseUrl !== '#') {
                            const urlObj = new URL(baseUrl);
                            const params = urlObj.searchParams;

                            // Origem fixa para cards de destaque
                            params.set('origem_motor', 'LP de Oferta');

                            // utm_source com código do afiliado cadastrado no widget
                            if (codigoAfiliado) {
                                params.set('utm_source', codigoAfiliado);
                            }

                            // Adiciona utm_medium fixo
                            params.set('utm_medium', 'afiliado-site');

                            // Conteúdo fixo para cards de destaque
                            params.set('utm_content', 'banner-destaque');

                            // Termo dinâmico conforme widget-modular
                            if (utmTerm) {
                                params.set('utm_term', utmTerm);
                            }

                            // Adiciona utm_campaign com o slug
                            if (card.Link.slug) {
                                params.set('utm_campaign', card.Link.slug);
                            }

                            cardLink = urlObj.toString();
                        } else {
                            cardLink = baseUrl;
                        }
                    } else if (typeof card.Link === 'string') {
                        // Formato antigo: Link é uma string simples
                        cardLink = card.Link;
                    }
                }

                return {
                    id: card.id,
                    categoria: card.Categoria || '',
                    titulo: card.titulo || '',
                    link: cardLink,
                    linkInfo: linkInfo,  // Informações completas do link (se disponível)
                    texto_botao: card.Texto_botao || 'Saiba Mais',
                    image: cardImage
                };
            });
        }

        // Editor HTML
        if (config.editor_html && widgetData.Editor_HTML) {
            components.editor_html = {
                id: widgetData.Editor_HTML.id,
                content: widgetData.Editor_HTML.content || ''
            };
        }

        // Footer (agora é string direta, não objeto)
        if (config.footer && widgetData.Footer) {
            // Footer pode ser string ou objeto
            components.footer = {
                texto_copy: typeof widgetData.Footer === 'string'
                    ? widgetData.Footer
                    : (widgetData.Footer.texto_copy || '')
            };
        }

        // Oferta Formulário
        if (config.ofertaFormulario && widgetData.oferta_formulario) {
            // Oferta formulário pode ser um array ou um objeto
            const ofertaData = Array.isArray(widgetData.oferta_formulario)
                ? widgetData.oferta_formulario[0]
                : widgetData.oferta_formulario;

            if (ofertaData) {
                components.ofertaFormulario = ofertaData;
            }
        }

        // Formulário Simples
        if (config.formulario_simples && widgetData.formulario_simples) {
            const formularioData = Array.isArray(widgetData.formulario_simples)
                ? widgetData.formulario_simples[0]
                : widgetData.formulario_simples;

            if (formularioData) {
                components.formularioSimples = formularioData;
            }
        }

        // Minimal Data (usa o mesmo renderizador de Formulário Simples)
        if (config.minimal_data && widgetData.minimal_data) {
            const minimalData = Array.isArray(widgetData.minimal_data)
                ? widgetData.minimal_data[0]
                : widgetData.minimal_data;

            if (minimalData) {
                const selectOptions = Array.isArray(minimalData.ofertas)
                    ? minimalData.ofertas
                        .map((oferta) => {
                            const label = oferta?.nome_campanha || oferta?.label || '';
                            const value = oferta?.idcampaing || oferta?.value || label;

                            if (!label || !value) return null;

                            return {
                                label,
                                value,
                                campaignIdentifier: oferta?.idcampaing || value,
                                slug: toSlug(oferta?.nome_campanha || label)
                            };
                        })
                        .filter(Boolean)
                    : [];

                components.minimal_data = {
                    isMinimalData: true,
                    utmTerm: widgetData.utm_term || null,
                    title: minimalData.titulo || '',
                    description: minimalData.descricao || '',
                    ctaText: minimalData.texto_botao || 'Quero receber ofertas',
                    colorTitle: minimalData.cor_titulo_descricao || '#ffffff',
                    colorText: minimalData.cor_titulo_descricao || '#ffffff',
                    colorBackground: minimalData.cor_background || '#222222',
                    color: minimalData.cor_botao || '#FF3C0D',
                    colorButtonText: minimalData.cor_texto_botao || '#ffffff',
                    selectPlaceholder: 'Selecione uma oferta',
                    selectName: 'campaignIdentifier',
                    selectOptions,
                    raw: minimalData
                };
            }
        }

        // Marcas (MarcasModule)
        if (config.marcas && widgetData.Marcas && Array.isArray(widgetData.Marcas) && widgetData.Marcas.length > 0) {
            components.marcas = {
                titulo: 'Marcas Parceiras',
                badge: `${widgetData.Marcas.length} MARCAS`,
                marcas: widgetData.Marcas.map(marca => ({
                    nome: marca.name || '',
                    logo: marca.logo || '',
                    descricao: marca.description || null
                }))
            };
        }

        return components;
    }

    /**
     * Busca e processa widget completo com componentes ativos
     * @param {string} documentId - Document ID do widget
     * @param {Object} componentConfig - Configuração de componentes (ex: {cards: true, bloco_imagem_texto: true})
     * @returns {Promise<Object>} Widget com componentes processados
     */
    async fetchWidget(documentId, componentConfig = {}) {
        try {
            const widgetData = await this.getByDocumentId(documentId);
            const utmTerm = widgetData.utm_term ||
                widgetData.slug ||
                (widgetData.Nome_widget || '')
                    .normalize('NFD')
                    .replace(/[\u0300-\u036f]/g, '')
                    .toLowerCase()
                    .replace(/[^a-z0-9]+/g, '-')
                    .replace(/^-+|-+$/g, '');

            const processed = {
                id: widgetData.id,
                documentId: widgetData.documentId,
                nome: widgetData.Nome_widget || '',
                codigo_afiliado: widgetData.codigo_afiliado || null,
                createdAt: widgetData.createdAt,
                updatedAt: widgetData.updatedAt,
                publishedAt: widgetData.publishedAt,
                components: this.extractActiveComponents(widgetData, componentConfig, widgetData.codigo_afiliado, utmTerm)
            };

            return processed;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Busca widget por ID com todos os componentes populados
     * @param {number|string} id - ID do widget
     * @param {Object} options - Opções adicionais
     * @returns {Promise<Object>}
     */
    async getById(id, options = {}) {
        try {
            const params = {
                populate: options.populate || '*',
                ...options.params
            };

            const data = await this.strapi.get(`${this.endpoint}/${id}`, params);

            // Transforma dados do Strapi v4
            const transformed = transformStrapiV4(data);

            return transformed;
        } catch (error) {
            console.error(`[WidgetModularService] Error loading widget ${id}:`, error);
            throw error;
        }
    }

    /**
     * Busca todos os widgets (com filtros opcionais)
     * @param {Object} options - Opções de filtro e paginação
     * @returns {Promise<Array>}
     */
    async getAll(options = {}) {
        try {
            const params = {
                populate: options.populate || '*',
                sort: options.sort || 'createdAt:desc',
                'pagination[page]': options.page || 1,
                'pagination[pageSize]': options.pageSize || 25,
                ...options.filters,
                ...options.params
            };

            const data = await this.strapi.get(this.endpoint, params);

            // Transforma dados
            const transformed = transformStrapiV4(data);

            return transformed;
        } catch (error) {
            console.error('[WidgetModularService] Error loading widgets:', error);
            throw error;
        }
    }

    /**
     * Busca widget por slug
     * @param {string} slug - Slug do widget
     * @param {Object} options - Opções adicionais
     * @returns {Promise<Object|null>}
     */
    async getBySlug(slug, options = {}) {
        try {
            const params = {
                populate: options.populate || '*',
                'filters[slug][$eq]': slug,
                ...options.params
            };

            const data = await this.strapi.get(this.endpoint, params);
            const transformed = transformStrapiV4(data);

            // Retorna primeiro resultado ou null
            const widget = Array.isArray(transformed) && transformed.length > 0
                ? transformed[0]
                : null;

            return widget;
        } catch (error) {
            console.error(`[WidgetModularService] Error loading widget with slug '${slug}':`, error);
            throw error;
        }
    }

    /**
     * Busca widgets por tipo
     * @param {string} type - Tipo do widget (ex: 'carousel', 'banner', 'gallery')
     * @param {Object} options - Opções adicionais
     * @returns {Promise<Array>}
     */
    async getByType(type, options = {}) {
        try {
            const params = {
                populate: options.populate || '*',
                'filters[type][$eq]': type,
                sort: options.sort || 'createdAt:desc',
                'pagination[page]': options.page || 1,
                'pagination[pageSize]': options.pageSize || 25,
                ...options.params
            };

            const data = await this.strapi.get(this.endpoint, params);
            const transformed = transformStrapiV4(data);

            return transformed;
        } catch (error) {
            console.error(`[WidgetModularService] Error loading widgets of type '${type}':`, error);
            throw error;
        }
    }

    /**
     * Busca widgets ativos/publicados
     * @param {Object} options - Opções adicionais
     * @returns {Promise<Array>}
     */
    async getActive(options = {}) {
        try {
            const params = {
                populate: options.populate || '*',
                'filters[active][$eq]': true,
                'filters[publishedAt][$notNull]': true,
                sort: options.sort || 'createdAt:desc',
                'pagination[page]': options.page || 1,
                'pagination[pageSize]': options.pageSize || 25,
                ...options.params
            };

            const data = await this.strapi.get(this.endpoint, params);
            const transformed = transformStrapiV4(data);

            return transformed;
        } catch (error) {
            console.error('[WidgetModularService] Error loading active widgets:', error);
            throw error;
        }
    }

    /**
     * Cria novo widget (requer autenticação)
     * @param {Object} data - Dados do widget
     * @returns {Promise<Object>}
     */
    async create(data) {
        try {
            const response = await this.strapi.post(this.endpoint, { data });
            const transformed = transformStrapiV4(response);

            return transformed;
        } catch (error) {
            console.error('[WidgetModularService] Error creating widget:', error);
            throw error;
        }
    }

    /**
     * Atualiza widget existente (requer autenticação)
     * @param {number|string} id - ID do widget
     * @param {Object} data - Dados para atualizar
     * @returns {Promise<Object>}
     */
    async update(id, data) {
        try {
            const response = await this.strapi.put(`${this.endpoint}/${id}`, { data });
            const transformed = transformStrapiV4(response);

            // Limpa cache após update
            this.strapi.clearCache();

            return transformed;
        } catch (error) {
            console.error(`[WidgetModularService] Error updating widget ${id}:`, error);
            throw error;
        }
    }

    /**
     * Remove widget (requer autenticação)
     * @param {number|string} id - ID do widget
     * @returns {Promise<void>}
     */
    async delete(id) {
        try {
            await this.strapi.delete(`${this.endpoint}/${id}`);

            // Limpa cache após delete
            this.strapi.clearCache();
        } catch (error) {
            console.error(`[WidgetModularService] Error deleting widget ${id}:`, error);
            throw error;
        }
    }

    /**
     * Busca componentes específicos de um widget
     * @param {number|string} id - ID do widget
     * @param {string} componentName - Nome do componente (ex: 'carousel', 'banner')
     * @returns {Promise<Object>}
     */
    async getComponents(id, componentName = null) {
        try {
            const populate = componentName
                ? `populate[${componentName}]=*`
                : 'populate=*';

            const data = await this.strapi.get(`${this.endpoint}/${id}?${populate}`);
            const transformed = transformStrapiV4(data);

            const components = componentName
                ? transformed[componentName]
                : transformed;

            return components;
        } catch (error) {
            console.error(`[WidgetModularService] Error loading components for widget ${id}:`, error);
            throw error;
        }
    }

    /**
     * Limpa cache do serviço
     */
    clearCache() {
        this.strapi.clearCache();
    }

    /**
     * Obtém estatísticas do cache
     * @returns {Object}
     */
    getCacheStats() {
        return {
            size: this.strapi.cache.size,
            enabled: this.strapi.enableCache,
            expiry: this.strapi.cacheExpiry
        };
    }
}

// Instância singleton
const widgetModularService = new WidgetModularService();

export default widgetModularService;
export { WidgetModularService };
